/**
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */
import React, { useState, useEffect, useRef } from "react";
import { RagGallery } from "../Gallery/RagGallery";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch } from "react-redux";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { ArrowUpDown } from "lucide-react";
import { useTheme } from "../Theme/ThemeProvider";
import { setRagChatType, clearRagHistory } from "../../store/chatModeSlice";
import { clearTranslationData, clearUploadedFiles } from "../../store/chatSlice";

import { getVisibleRagOptions, type RagChatType } from "../../utils/rag/ragConfig";

interface RagItem {
  key: RagChatType;
  label: string;
  subtitle: string;
  icon: React.ReactNode;
}

// Separate component so useState hover is isolated per-button and
// never conflicts with Framer Motion's inline style writes on the wrapper.
function RagButton({ item, index, textColor, secondaryColor, isDark, onSelect, wrapperClassName }: {
  item: RagItem;
  index: number;
  textColor: string;
  secondaryColor: string;
  isDark: boolean;
  onSelect: (key: RagChatType) => void;
  wrapperClassName?: string;
}) {
  const [hovered, setHovered] = useState(false);

  // Light mode: white card with subtle gray shadow; Dark mode: translucent white overlay
  const bgDefault = isDark
    ? "radial-gradient(circle at center, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.02) 100%)"
    : "linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)";
  const bgHovered = isDark
    ? "radial-gradient(circle at center, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.05) 100%)"
    : "linear-gradient(145deg, #f3f4f6 0%, #e5e7eb 100%)"; // light mode: explicit neutral gray
  const shadowDefault = isDark
    ? "0 1px 2px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.1)"
    : "0 2px 6px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.9)";
  const shadowHovered = isDark
    ? `0 4px 12px rgba(0,0,0,0.3), inset 0 0 12px rgba(255,255,255,0.1)`
    : `0 6px 18px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,1)`;

  // Border gradient: dark uses white shimmer; light only shows border on hover
  const borderDefault = isDark
    ? "linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.25) 20%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.25) 80%, rgba(255,255,255,0.6) 100%)"
    : "none";
  const borderHovered = isDark
    ? "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.5) 20%, rgba(255,255,255,0.1) 40%, rgba(255,255,255,0.1) 60%, rgba(255,255,255,0.5) 80%, rgba(255,255,255,0.9) 100%)"
    : "linear-gradient(135deg, rgba(17,24,39,0.7) 0%, rgba(17,24,39,0.35) 20%, rgba(17,24,39,0.1) 40%, rgba(17,24,39,0.1) 60%, rgba(17,24,39,0.35) 80%, rgba(17,24,39,0.7) 100%)";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: index * 0.05, ease: "easeOut" }}
      className={`w-full h-full${wrapperClassName ? ` ${wrapperClassName}` : ''}`}
    >
      <button
        onClick={() => onSelect(item.key)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="relative overflow-visible rounded-2xl w-full h-full py-4 pl-4 pr-6 flex flex-row items-center gap-4 group cursor-pointer active:scale-95"
        style={{
          background: hovered ? bgHovered : bgDefault,
          boxShadow: hovered ? shadowHovered : shadowDefault,
          transform: hovered ? "scale(1.05) translateY(-4px)" : "scale(1) translateY(0px)",
          transition: "transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease",
        }}
      >
        {/* Gradient border, same mask trick as menu */}
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            background: hovered ? borderHovered : borderDefault,
            mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            maskComposite: "xor",
            WebkitMaskComposite: "xor",
            padding: "1.5px",
            zIndex: -1,
          }}
        />
        {/* Icon left aligned */}
        <div className="flex-shrink-0 transition-all group-hover:scale-110">
          {item.icon}
        </div>
        {/* Title + subtitle stacked */}
        <div className="flex flex-col items-start text-left">
          <p
            className="font-bold whitespace-nowrap"
            style={{ fontFamily: "3MCircularTT", color: textColor, fontSize: "clamp(0.85rem, 1.1vw, 1rem)" }}
          >
            {item.label}
          </p>
          <p
            className="line-clamp-4 break-words leading-tight text-xs"
            style={{ fontFamily: "3MCircularTT", color: secondaryColor }}
          >
            {item.subtitle}
          </p>
        </div>
      </button>
    </motion.div>
  );
}

const SORT_OPTIONS = [
  { id: 'default' as const, label: 'Default' },
  { id: 'alphabetical' as const, label: 'Sort A\u2013Z' },
];

function SortDropdown({ sortMode, onSortChange, isDark, secondaryColor }: {
  sortMode: 'default' | 'alphabetical';
  onSortChange: (mode: 'default' | 'alphabetical') => void;
  isDark: boolean;
  secondaryColor: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedOption = SORT_OPTIONS.find(o => o.id === sortMode) ?? SORT_OPTIONS[0];

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative inline-block" style={{ zIndex: 100 }}>
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors border border-[var(--color-border)]"
        style={{
          minWidth: '140px',
          color: isDark ? 'rgba(255, 255, 255, 0.85)' : secondaryColor,
          background: 'transparent',
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <ArrowUpDown size={16} className="flex-shrink-0" style={{ display: 'block' }} />
        <span className="pt-0.5">{selectedOption.label}</span>
        <ChevronDownIcon
          className="w-4 h-4 transition-transform flex-shrink-0"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', display: 'block' }}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scaleY: 0.8, y: -4 }}
            animate={{ opacity: 1, scaleY: 1, y: 0 }}
            exit={{ opacity: 0, scaleY: 0.8, y: -4 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute right-0 mt-1 rounded-lg shadow-lg overflow-hidden origin-top py-1"
            style={{
              minWidth: '140px',
              width: containerRef.current?.offsetWidth ?? 'auto',
              background: isDark ? 'rgba(30, 30, 30, 0.95)' : '#FFFFFF',
              border: '1px solid var(--color-border)',
              backdropFilter: 'blur(12px)',
            }}
            role="listbox"
            aria-activedescendant={sortMode}
          >
            {SORT_OPTIONS.map((option) => {
              const isSelected = option.id === sortMode;
              return (
                <button
                  key={option.id}
                  id={option.id}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onSortChange(option.id);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center px-3 py-2 text-sm text-left transition-colors"
                  style={{
                    color: isSelected
                      ? (isDark ? '#82B6FA' : '#2563EB')
                      : (isDark ? 'rgba(255, 255, 255, 0.7)' : '#374151'),
                    background: 'transparent',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255, 255, 255, 0.08)' : '#E8F3FF'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>{option.label}</span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const ITEMS_PER_PAGE = 8;

export function KnowledgeRagSelector({ sharedPaddingClass }: { sharedPaddingClass: string }) {
  const dispatch = useDispatch();
  const { accent, mode } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageDirection, setPageDirection] = useState(0);
  const [sortMode, setSortMode] = useState<'default' | 'alphabetical'>('default');

  const getLightAccentTextColor = (a: string) => {
    if (a === "purple") return "#a78bfa";
    if (a === "blue") return "#60a5fa";
    if (a === "green") return "#34d399";
    return "#e5e7eb";
  };
  // In dark mode use the light accent colour for visual pop; in light mode always use neutral dark so themed accents don't bleed into button text/icons.
  const textColor = mode === 'dark' ? getLightAccentTextColor(accent) : '#111827';
    // Blue accent for subtitle text
    const blueAccent = mode === 'dark' ? "#38bdf8" : '#5E95ED'; ; // sky-400
    // #5E95ED
  const secondaryColor = mode === 'dark' ? '#9ca3af' : '#6b7280'; // gray-400 dark / gray-500 light

  const ragItems: RagItem[] = getVisibleRagOptions().map((cfg) => {
    const iconColor = cfg.key === 'TEBG' ? '#000000' : textColor;
    const iconElement = React.createElement(cfg.iconComponent, {
      ...cfg.iconProps,
      style: { color: iconColor },
    });

    return {
      key: cfg.key,
      label: cfg.label,
      subtitle: cfg.subtitle,
      icon: cfg.key === 'TEBG' ? (
        <div
          style={{
            background: '#ffffff',
            borderRadius: '8px',
            padding: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {iconElement}
        </div>
      ) : iconElement,
    };
  });
  const shouldShowSearch = ragItems.length > ITEMS_PER_PAGE;

  const filteredRagItems = (() => {
    const q = searchQuery.toLowerCase().trim();
    let items = q
      ? ragItems.filter(
          (item) =>
            item.label.toLowerCase().includes(q) ||
            item.subtitle.toLowerCase().includes(q)
        )
      : [...ragItems];
    if (sortMode === 'alphabetical') {
      items = [...items].sort((a, b) => a.label.localeCompare(b.label));
    }
    return items;
  })();
  const hasResults = filteredRagItems.length > 0;

  // Reset to first page when search query or sort changes
  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery, sortMode]);

  const totalPages = Math.max(1, Math.ceil(filteredRagItems.length / ITEMS_PER_PAGE));
  const hasMultiplePages = totalPages > 1;
  const paginatedItems = filteredRagItems.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage >= totalPages - 1;

  const handleSelect = (ragType: RagChatType) => {
    if (!ragType) return;
    dispatch(clearRagHistory());
    dispatch(clearTranslationData());
    dispatch(clearUploadedFiles());
    dispatch(setRagChatType(ragType));
  };

  // Render the new Gallery/Carousel component, passing necessary props.
  return (
    <RagGallery
      ragItems={ragItems}
      shouldShowSearch={shouldShowSearch}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      sortMode={sortMode}
      setSortMode={setSortMode}
      mode={mode}
      blueAccent={blueAccent}
      secondaryColor={secondaryColor}
      textColor={textColor}
      ITEMS_PER_PAGE={ITEMS_PER_PAGE}
      filteredRagItems={filteredRagItems}
      hasResults={hasResults}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      pageDirection={pageDirection}
      setPageDirection={setPageDirection}
      totalPages={totalPages}
      hasMultiplePages={hasMultiplePages}
      paginatedItems={paginatedItems}
      isFirstPage={isFirstPage}
      isLastPage={isLastPage}
      handleSelect={handleSelect}
      SortDropdown={SortDropdown}
      RagButton={RagButton}
    />
  );
}
